import { CampaignHomeBanner } from '../mkt/ReviewToEarnPage';
import { navigateToApp } from '../../auth/appNavigation';
import { appUrl } from '../../config/appUrl';
import './landing-reviewer.css';
import { ArrowRight, MapPin, PenLine, ChevronDown, Heart, Store, CheckCircle2 } from 'lucide-react';
import './hero-map.css';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from '../../api/http';
import { HeroMap } from './HeroMap';
import { PlaceCarousel } from './PlaceCarousel';
import { AdaptiveBlockBanner } from './AdaptiveBlockBanner';
import { AppleStoreIcon, openBeaw, StoreButtons } from './StoreButtons';
import type { LandingData } from './types';
import { formatDistance, storeConfig, track } from './types';
import { loadLandingData, readLandingCache, writeLandingCache, type LandingCity } from './landingApi';
import { PublicFooter, PublicHeader, usePublicLocale, type PublicLocale } from '../../layouts/PublicFrame';
import { LocationPicker } from './LocationPicker';

const LOCATION_STORAGE_KEY = 'beaw.ai.location';
type LandingLocationPreference = { city: string; cityLabel: string; source: 'default' | 'manual' | 'geo'; lat?: number; lng?: number };

function readLocationPreference(): LandingLocationPreference {
  try {
    const value = JSON.parse(localStorage.getItem(LOCATION_STORAGE_KEY) || '{}') as Record<string, unknown>;
    const location = (value.user_location || value.userLocation || {}) as Record<string, unknown>;
    const city = typeof value.city === 'string' ? value.city.trim() : '';
    if (city) return {
      city,
      cityLabel: typeof value.city_label === 'string' ? value.city_label : city,
      source: value.source === 'geo' ? 'geo' : 'manual',
      lat: Number.isFinite(Number(location.lat)) ? Number(location.lat) : undefined,
      lng: Number.isFinite(Number(location.lng)) ? Number(location.lng) : undefined,
    };
  } catch { /* Ignore malformed legacy browser storage. */ }
  return { city: storeConfig.defaultCity, cityLabel: storeConfig.defaultCity, source: 'default' };
}

function storeLocationPreference(preference: LandingLocationPreference) {
  localStorage.setItem(LOCATION_STORAGE_KEY, JSON.stringify({
    city: preference.city, city_label: preference.cityLabel, country: 'TH', country_code: 'TH',
    nearby: preference.source === 'geo', source: preference.source,
    ...(preference.lat !== undefined && preference.lng !== undefined ? { userLocation: { lat: preference.lat, lng: preference.lng }, user_location: { lat: preference.lat, lng: preference.lng } } : {}),
  }));
}

function localizedCity(city: string, locale: PublicLocale) {
  const normalized = city.trim().toLocaleLowerCase();
  if (normalized === 'chiang mai' || normalized === 'เชียงใหม่') return locale === 'th' ? 'เชียงใหม่' : 'Chiang Mai';
  return city;
}

export function LandingPage() {
  const [data, setData] = useState<LandingData | null>(null);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [preference, setPreference] = useState<LandingLocationPreference>(readLocationPreference);
  const [category, setCategory] = useState('coffee');
  const [activeIndex, setActiveIndex] = useState(0);
  const { locale, changeLocale } = usePublicLocale();
  const tracked = useRef(new Set<string>());
  const requestSequence = useRef(0);
  const autoLocationAttempted = useRef(false);
  const trackOnce = (key: string, event: string, props: Record<string, unknown>) => { if (!tracked.current.has(key)) { tracked.current.add(key); track(event, props); } };
  const load = useCallback(async (city: string, coords?: { lat: number; lng: number }) => {
    const request = ++requestSequence.current;
    setError('');
    const cached = readLandingCache(city, coords, locale, category);
    if (cached) {
      setData(cached.data); setActiveIndex(0);
      if (cached.fresh) { setRefreshing(false); return cached.data; }
    }
    setRefreshing(Boolean(coords));
    try {
      const next = await loadLandingData(city, coords, locale, category);
      writeLandingCache(city, coords, locale, category, next);
      // A background refresh keeps the carousel where the visitor left it.
      if (request === requestSequence.current) { setData(next); if (!cached) setActiveIndex(0); }
      return next;
    }
    catch (reason) { if (request === requestSequence.current) setError(reason instanceof ApiError ? reason.message : 'ไม่สามารถโหลดข้อมูลใกล้ตัวได้'); }
    finally { if (request === requestSequence.current) setRefreshing(false); }
    return null;
  }, [locale, category]);
  useEffect(() => {
    const coords = preference.lat !== undefined && preference.lng !== undefined ? { lat: preference.lat, lng: preference.lng } : undefined;
    void load(preference.city, coords);
    trackOnce('landing-view', 'landing_view', { city: preference.city, location_source: preference.source });
  }, [load, preference.city, preference.lat, preference.lng, preference.source]);
  const useLocation = (): Promise<boolean> => new Promise((resolve) => {
    if (!navigator.geolocation) { setError('อุปกรณ์นี้ไม่รองรับการอ่านตำแหน่ง กรุณาเลือกเมือง'); resolve(false); return; }
    setRefreshing(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        const next = await load(preference.city, { lat: coords.latitude, lng: coords.longitude });
        if (!next) { resolve(false); return; }
        const updated: LandingLocationPreference = {
          city: next.location.city,
          cityLabel: next.location.city_localized || next.location.city,
          source: 'geo', lat: coords.latitude, lng: coords.longitude,
        };
        storeLocationPreference(updated); setPreference(updated); resolve(true);
      },
      () => { setRefreshing(false); setError('อ่านตำแหน่งไม่สำเร็จ ลองเลือกเมืองเองได้'); resolve(false); }, { timeout: 7000, maximumAge: 300000 },
    );
  });
  useEffect(() => {
    if (preference.source !== 'default' || autoLocationAttempted.current) return;
    autoLocationAttempted.current = true;
    void useLocation();
  }, [preference.source]);
  const selectCity = (selected: LandingCity) => {
    const updated: LandingLocationPreference = { city: selected.name, cityLabel: selected.name_local, source: 'manual' };
    storeLocationPreference(updated); setPreference(updated); setError('');
  };
  const city = data?.location.city || preference.city;
  const cityLabel = (locale === 'th' ? data?.location.city_localized || preference.cityLabel : '') || localizedCity(city, locale);
  const landingMapPlaces = (data?.nearby_places || []).map((place) => ({ ...place }));
  const mapPlacesById = new Map(landingMapPlaces.map((place) => [place.place_id, place]));
  (data?.campaigns || []).forEach((campaign, index) => {
    const existing = mapPlacesById.get(campaign.place_id);
    if (existing) {
      existing.has_campaign = true;
      existing.campaign = { campaign_id: campaign.campaign_id, reward_points: campaign.reward_points, remaining_slots: campaign.remaining_slots };
      if (!existing.image_url && campaign.image_url) existing.image_url = campaign.image_url;
      return;
    }
    landingMapPlaces.push({ place_id: campaign.place_id, name: campaign.place_name, lat: null, lng: null, distance_m: campaign.distance_m, category: campaign.category, image_url: campaign.image_url, rating: null, review_count: 0, rank: 100 + index, selected: false, has_campaign: true, campaign: { campaign_id: campaign.campaign_id, reward_points: campaign.reward_points, remaining_slots: campaign.remaining_slots } });
  });
  const carouselPlaces = landingMapPlaces.filter(place => place.place_id).slice(0, 14);
  return <div className="landing landing--reviewer">
    
    <PublicHeader locale={locale} onLocaleChange={changeLocale} downloadAction={storeConfig.ios ? <a className="hx-header-download" href={storeConfig.ios} onClick={() => track('appstore_click', { source_section: 'header', city })}><AppleStoreIcon />ดาวน์โหลดแอป</a> : undefined} mobileLocation={<button type="button" className="reviewer-header-city" aria-label={`เลือกเมือง ${cityLabel}`} onClick={() => setPickerOpen(true)}><MapPin size={16}/><span>{cityLabel}</span><ChevronDown size={14}/></button>} />

    <main>
      <section className="hx-hero" id="for-users">
        <div className="hx-hero__copy">
          <p className="eyebrow">BEAW.CLUB · BE A REVIEWER.</p>
          <h1 className="hx-hero__title">ทุกแก้ว ทุกมื้อ คือรายได้<br /><span>เป็นครีเอเตอร์เขียนรีวิว</span></h1>
          <p className="hx-hero__lead">ไปกิน ไปลอง แล้วเล่าประสบการณ์จริง<br />วันนี้ <strong>10 บาท</strong> ต่อรีวิว *</p>
          <div className="hx-hero__actions">
            <a className="button hx-hero__primary" href={appUrl('/commu/create')}><PenLine />เริ่มรีวิวเลย <ArrowRight /></a>
            <a className="button hx-hero__secondary" href={appUrl('/commu')}><MapPin />ดูรีวิวใกล้ฉัน</a>
          </div>
          <div className="hx-hero__download"><StoreButtons section="hero" compact /><small>*รีวิวที่ผ่านเงื่อนไข 10 pts ≈ 10 บาท</small></div>
          {error && <p className="inline-notice" role="status">{error}</p>}
        </div>
        <div className="hx-hero__map">
          <HeroMap places={carouselPlaces} city={city} cityLabel={cityLabel} locale={locale} category={category} onCategory={setCategory} onPickCity={() => setPickerOpen(true)} activePlaceId={carouselPlaces[activeIndex]?.place_id} onSelectPlace={place => setActiveIndex(Math.max(0, carouselPlaces.findIndex(item => item.place_id === place.place_id)))} />
        </div>
        <div className="hx-hero__carousel"><PlaceCarousel places={carouselPlaces} activeIndex={Math.min(activeIndex, Math.max(0, carouselPlaces.length - 1))} onActiveIndex={setActiveIndex} cityLabel={cityLabel} /></div>
      </section>
      <AdaptiveBlockBanner blocks={data?.adaptive_blocks || []} locale={locale} city={city} />
      <CampaignHomeBanner />
      <section className="home-audience-banner" id="download" aria-label="ร่วมเป็นส่วนหนึ่งของ Beaw">
        <div className="home-audience-banner__intro">
          <span className="home-audience-banner__gift" aria-hidden="true">🎁</span>
          <div><h2>เปิด <em>Beaw</em><br />แล้วออกไปสำรวจ{cityLabel}</h2></div>
        </div>
        <div className="home-audience-banner__visual" aria-hidden="true">
          <div className="home-audience-banner__bubbles">
            {[['☕', 'รีวิวกาแฟ', 10], ['🍜', 'ลองร้านใหม่', 15], ['🏯', 'รีวิวที่เที่ยว', 5]].map(([icon, label, reward], index) => <div className={`home-audience-banner__bubble home-audience-banner__bubble--${index}`} key={label}><span>{icon}</span><div><b>{label}</b><strong>+{reward} บาท <Heart size={13} fill="currentColor" /></strong></div></div>)}
          </div>
        </div>
        <div className="home-audience-banner__lane">
          <a className="home-audience-banner__button" href="/for-creator"><PenLine/><span>สำหรับครีเอเตอร์</span><ArrowRight/></a>
          <h3>เริ่มรีวิวร้านใกล้คุณ สร้างรายได้เลย</h3>
          <ul><li><CheckCircle2/>รับ 10 บาท ต่อรีวิว*</li><li><CheckCircle2/>ค้นหาร้านใกล้ตัว</li><li><CheckCircle2/>แชร์ประสบการณ์ได้ทันที</li></ul>
          <small>*สำหรับรีวิวที่ผ่านเงื่อนไข</small>
        </div>
        <div className="home-audience-banner__lane home-audience-banner__lane--merchant">
          <span className="home-audience-banner__badge">สิทธิพิเศษสำหรับ 100 ร้านแรก</span>
          <a className="home-audience-banner__button home-audience-banner__button--outline" href="/for-merchant"><Store/><span>สำหรับร้านค้า</span><ArrowRight/></a>
          <h3>ลงร้านกับเรา เพิ่มลูกค้า เพิ่มยอดขาย</h3>
          <ul><li><CheckCircle2/>เข้าถึงครีเอเตอร์ในพื้นที่</li><li><CheckCircle2/>เพิ่มการมองเห็นร้าน</li><li><CheckCircle2/>ง่าย รวดเร็ว ไม่มีค่าใช้จ่าย</li></ul>
        </div>
      </section>

      {Boolean(data?.community_voices.length) && <section className="voices section-card" id="community"><div className="section-heading"><button className="section-heading__web-link" onClick={() => openBeaw('community-heading')}><h2>คนแถวนี้กำลังพูดถึงอะไร</h2></button><button onClick={() => openBeaw('community')}>สำรวจรีวิวใกล้คุณ <ArrowRight /></button></div><div className="voice-grid">{data!.community_voices.map((voice) => <button className="voice" key={voice.review_id} onClick={() => { track('community_quote_click', { review_id: voice.review_id, place_id: voice.place_id, city }); void navigateToApp(appUrl(`/commu/post/${encodeURIComponent(voice.review_id)}`)); }}><div className="voice__image">{voice.image_url ? <img src={voice.image_url} alt="" /> : <PenLine />}</div><blockquote>“{voice.quote}”</blockquote><p>{voice.place_name} · {formatDistance(voice.distance_m)}</p></button>)}</div></section>}


    </main>
    <PublicFooter />
    <LocationPicker open={pickerOpen} locale={locale} selectedCity={preference.city} refreshing={refreshing} onClose={() => setPickerOpen(false)} onSelect={selectCity} onUseCurrent={useLocation} />
  </div>;
}
